import { Company, TRIZPrinciple, Feature, ProductRoadmap, WorkflowNode, DashboardStats, ZachmanLayer } from '../types';

export const demoCompanies: Company[] = [
  {
    id: 'comp-1',
    name: 'Skyflow',
    industry: 'Data Privacy',
    size: 'medium',
    description: 'Data privacy vault service for PII protection and compliance',
    currentChallenges: [
      'Increasing data privacy regulations',
      'Balancing security with data usability',
      'Integration with existing enterprise systems',
      'Customer education on data privacy importance'
    ],
    marketPosition: 'Leading data privacy vault provider for sensitive PII data',
    competitors: ['Protegrity', 'Very Good Security', 'Amazon Macie', 'IBM Security Guardium'],
    techStack: ['Golang', 'React', 'Kubernetes', 'AWS', 'Vault', 'Zero-Trust Architecture'],
    budget: '$8M annually',
    timeline: '12 months for global privacy solution expansion'
  }
];

export const trizPrinciples: TRIZPrinciple[] = [
  {
    id: 1,
    name: 'Segmentation',
    description: 'Divide an object into independent parts',
    examples: ['Modular software architecture', 'Microservices', 'Component-based UI'],
    applicability: ['Scalability', 'Maintainability', 'Team autonomy']
  },
  {
    id: 2,
    name: 'Taking Out',
    description: 'Separate interfering parts or properties',
    examples: ['API Gateway', 'Separation of concerns', 'Containerization'],
    applicability: ['Performance', 'Security', 'Debugging']
  },
  {
    id: 15,
    name: 'Dynamics',
    description: 'Make objects or systems adaptive',
    examples: ['Auto-scaling', 'Responsive design', 'AI-driven personalization'],
    applicability: ['User experience', 'Performance optimization', 'Resource efficiency']
  },
  {
    id: 17,
    name: 'Another Dimension',
    description: 'Move to multi-dimensional space',
    examples: ['3D visualization', 'Multi-tenant architecture', 'Cross-platform development'],
    applicability: ['User engagement', 'Market expansion', 'Technical versatility']
  },
  {
    id: 35,
    name: 'Parameter Changes',
    description: 'Change physical or chemical states',
    examples: ['Progressive web apps', 'Edge computing', 'Serverless architecture'],
    applicability: ['Performance', 'Accessibility', 'Cost optimization']
  },
  {
    id: 24,
    name: 'Intermediary',
    description: 'Use an intermediary carrier or process',
    examples: ['Data tokenization', 'Proxy services', 'Zero-knowledge proofs'],
    applicability: ['PII protection', 'Data security', 'Privacy compliance']
  },
  {
    id: 9,
    name: 'Preliminary Anti-Action',
    description: 'Preemptively counteract negative effects',
    examples: ['Data encryption', 'Access control policies', 'Privacy by design'],
    applicability: ['Regulatory compliance', 'Data breaches prevention', 'Trust building']
  }
];

export const workflowNodes: WorkflowNode[] = [
  {
    id: 'start',
    name: 'Start Project',
    description: 'Initialize new product roadmap project',
    type: 'start',
    position: { x: 80, y: 80 },
    connections: ['research'],
    status: 'completed',
    details: 'Project initialization with basic setup and team assignment',
    nextSteps: ['Company research', 'Stakeholder identification']
  },
  {
    id: 'research',
    name: 'Company Research',
    description: 'Gather comprehensive company information',
    type: 'process',
    position: { x: 280, y: 80 },
    connections: ['market-analysis'],
    status: 'completed',
    details: 'Deep dive into company background, challenges, and objectives',
    nextSteps: ['Market analysis', 'Competitive landscape research']
  },
  {
    id: 'market-analysis',
    name: 'Market Analysis',
    description: 'Analyze market trends and opportunities',
    type: 'process',
    position: { x: 480, y: 80 },
    connections: ['problem-analysis'],
    status: 'completed',
    details: 'Market size, trends, customer segments, and growth opportunities',
    nextSteps: ['Problem identification', 'Opportunity mapping']
  },
  {
    id: 'problem-analysis',
    name: 'Problem Analysis',
    description: 'Identify core problems and contradictions',
    type: 'process',
    position: { x: 680, y: 80 },
    connections: ['triz-innovation'],
    status: 'available',
    details: 'Root cause analysis and problem formulation using systematic approach',
    nextSteps: ['TRIZ analysis', 'Solution ideation']
  },
  {
    id: 'triz-innovation',
    name: 'TRIZ Innovation',
    description: 'Apply TRIZ principles for innovative solutions',
    type: 'ai',
    position: { x: 880, y: 80 },
    connections: ['feature-definition'],
    status: 'locked',
    details: 'AI-powered TRIZ analysis to generate innovative solution concepts',
    nextSteps: ['Feature specification', 'Solution validation']
  },
  {
    id: 'feature-definition',
    name: 'Feature Definition',
    description: 'Define specific product features and requirements',
    type: 'process',
    position: { x: 1080, y: 80 },
    connections: ['architecture'],
    status: 'locked',
    details: 'Detailed feature specifications with acceptance criteria',
    nextSteps: ['System architecture', 'Technical design']
  },
  {
    id: 'architecture',
    name: 'System Architecture',
    description: 'Design technical architecture using Zachman Framework',
    type: 'process',
    position: { x: 200, y: 300 },
    connections: ['prioritization'],
    status: 'locked',
    details: 'Multi-layered architecture design with clear separation of concerns',
    nextSteps: ['Feature prioritization', 'Resource planning']
  },
  {
    id: 'prioritization',
    name: 'Prioritization',
    description: 'Prioritize features using impact vs effort matrix',
    type: 'decision',
    position: { x: 400, y: 300 },
    connections: ['timeline'],
    status: 'locked',
    details: 'Strategic prioritization based on business value and technical complexity',
    nextSteps: ['Timeline planning', 'Resource allocation']
  },
  {
    id: 'timeline',
    name: 'Timeline Planning',
    description: 'Create detailed development timeline',
    type: 'process',
    position: { x: 600, y: 300 },
    connections: ['review'],
    status: 'locked',
    details: 'Gantt-style timeline with milestones and dependencies',
    nextSteps: ['Stakeholder review', 'Team alignment']
  },
  {
    id: 'review',
    name: 'Stakeholder Review',
    description: 'Review and validate roadmap with stakeholders',
    type: 'decision',
    position: { x: 800, y: 300 },
    connections: ['iterate'],
    status: 'locked',
    details: 'Comprehensive review with key stakeholders and decision makers',
    nextSteps: ['Feedback incorporation', 'Final approval']
  },
  {
    id: 'iterate',
    name: 'Iterate & Refine',
    description: 'Incorporate feedback and refine roadmap',
    type: 'process',
    position: { x: 1000, y: 300 },
    connections: ['deploy'],
    status: 'locked',
    details: 'Iterative refinement based on stakeholder feedback',
    nextSteps: ['Final roadmap', 'Deployment preparation']
  },
  {
    id: 'deploy',
    name: 'Deploy Roadmap',
    description: 'Finalize and deploy the product roadmap',
    type: 'end',
    position: { x: 600, y: 450 },
    connections: [],
    status: 'locked',
    details: 'Final roadmap deployment with team communication and documentation',
    nextSteps: ['Implementation begins', 'Progress tracking']
  }
];

export const zachmanLayers: ZachmanLayer[] = [
  {
    id: 'business',
    name: 'Business Architecture',
    description: 'Business processes, goals, and requirements',
    perspective: 'business',
    components: [
      {
        id: 'bus-1',
        name: 'Business Process Optimization',
        description: 'Streamline core business workflows',
        what: 'Automated workflow processes',
        how: 'AI-powered process automation',
        where: 'Cloud-based SaaS platform',
        who: 'Business users and administrators',
        when: 'Q1-Q2 2024',
        why: 'Increase efficiency and reduce manual work',
        status: 'planned'
      }
    ]
  },
  {
    id: 'application',
    name: 'Application Architecture',
    description: 'Software applications and their interactions',
    perspective: 'application',
    components: [
      {
        id: 'app-1',
        name: 'Microservices Architecture',
        description: 'Scalable microservices-based application design',
        what: 'Independent service modules',
        how: 'Container-based deployment',
        where: 'Kubernetes clusters',
        who: 'Development teams',
        when: 'Q2-Q3 2024',
        why: 'Enable scalability and independent development',
        status: 'in-progress'
      }
    ]
  },
  {
    id: 'data',
    name: 'Data Architecture',
    description: 'Data models, storage, and governance',
    perspective: 'data',
    components: [
      {
        id: 'data-1',
        name: 'Data Lake Implementation',
        description: 'Centralized data storage and analytics platform',
        what: 'Unified data repository',
        how: 'ETL pipelines and data processing',
        where: 'Cloud data centers',
        who: 'Data engineers and analysts',
        when: 'Q3-Q4 2024',
        why: 'Enable data-driven decision making',
        status: 'planned'
      }
    ]
  },
  {
    id: 'technology',
    name: 'Technology Architecture',
    description: 'Technology platforms and standards',
    perspective: 'technology',
    components: [
      {
        id: 'tech-1',
        name: 'Cloud-Native Platform',
        description: 'Modern cloud-native technology stack',
        what: 'Cloud services and APIs',
        how: 'Infrastructure as Code',
        where: 'Multi-cloud environment',
        who: 'DevOps and platform teams',
        when: 'Q1-Q4 2024',
        why: 'Ensure scalability and reliability',
        status: 'in-progress'
      }
    ]
  },
  {
    id: 'infrastructure',
    name: 'Infrastructure Architecture',
    description: 'Physical and virtual infrastructure',
    perspective: 'infrastructure',
    components: [
      {
        id: 'infra-1',
        name: 'Auto-Scaling Infrastructure',
        description: 'Dynamic infrastructure scaling based on demand',
        what: 'Elastic compute resources',
        how: 'Auto-scaling groups and load balancers',
        where: 'Global cloud regions',
        who: 'Infrastructure team',
        when: 'Q2-Q3 2024',
        why: 'Optimize costs and performance',
        status: 'planned'
      }
    ]
  }
];

export const demoFeatures: Feature[] = [
  {
    id: 'feat-5',
    name: 'Enterprise Security Suite',
    description: 'Advanced security features including SSO, RBAC, and audit logs',
    priority: 'critical',
    effort: 8,
    impact: 9,
    category: 'Security',
    dependencies: [],
    estimatedHours: 400,
    assignedTeam: 'Security Team',
    status: 'planning',
    quarter: 'Q1 2024'
  },
  {
    id: 'feat-6',
    name: 'PII Data Vault',
    description: 'Secure vault for storing and tokenizing sensitive PII data',
    priority: 'critical',
    effort: 9,
    impact: 10,
    category: 'Data Privacy',
    dependencies: [],
    estimatedHours: 560,
    assignedTeam: 'Security Team',
    status: 'development',
    quarter: 'Q1 2024'
  },
  {
    id: 'feat-7',
    name: 'Zero-Trust PII Access Controls',
    description: 'Granular policy-based access controls for PII data with zero-trust architecture',
    priority: 'high',
    effort: 8,
    impact: 9,
    category: 'Data Privacy',
    dependencies: ['feat-6'],
    estimatedHours: 480,
    assignedTeam: 'Security Team',
    status: 'planning',
    quarter: 'Q2 2024'
  },
  {
    id: 'feat-8',
    name: 'Compliance Automation',
    description: 'Automated compliance with GDPR, CCPA, HIPAA and other privacy regulations',
    priority: 'high',
    effort: 7,
    impact: 9,
    category: 'Data Privacy',
    dependencies: ['feat-6'],
    estimatedHours: 420,
    assignedTeam: 'Compliance Team',
    status: 'planning',
    quarter: 'Q2 2024'
  },
  {
    id: 'feat-9',
    name: 'Data Tokenization API',
    description: 'Public API for seamless integration of PII tokenization services',
    priority: 'high',
    effort: 6,
    impact: 8,
    category: 'Data Privacy',
    dependencies: ['feat-6'],
    estimatedHours: 320,
    assignedTeam: 'API Team',
    status: 'backlog',
    quarter: 'Q3 2024'
  },
  {
    id: 'feat-10',
    name: 'Privacy Governance Dashboard',
    description: 'Comprehensive dashboard for monitoring privacy compliance and data usage',
    priority: 'medium',
    effort: 7,
    impact: 8,
    category: 'Data Privacy',
    dependencies: ['feat-6', 'feat-8'],
    estimatedHours: 380,
    assignedTeam: 'Frontend Team',
    status: 'backlog',
    quarter: 'Q3 2024'
  }
];

export const dashboardStats: DashboardStats = {
  totalRoadmaps: 1,
  activeProjects: 3,
  completedFeatures: 12,
  upcomingMilestones: 5,
  trizPrinciplesUsed: 7,
  avgInnovationScore: 8.7
};

export const demoRoadmap: ProductRoadmap = {
  id: 'roadmap-1',
  companyId: 'comp-4',
  name: 'Skyflow PII Protection Roadmap',
  description: 'Comprehensive product roadmap for Skyflow PII data protection platform',
  createdAt: '2024-01-15T10:00:00Z',
  updatedAt: '2024-01-20T15:30:00Z',
  features: demoFeatures.filter(f => f.category === 'Data Privacy' || f.category === 'Security'),
  timeline: {
    id: 'timeline-1',
    name: '2024 Development Timeline',
    features: demoFeatures.filter(f => f.category === 'Data Privacy' || f.category === 'Security'),
    startDate: '2024-01-01',
    endDate: '2024-12-31',
    milestones: [
      {
        id: 'milestone-1',
        name: 'Q1 PII Vault Launch',
        description: 'Launch of core PII data vault with tokenization',
        date: '2024-03-31',
        status: 'in-progress',
        dependencies: ['feat-6', 'feat-5']
      },
      {
        id: 'milestone-2',
        name: 'Q2 Zero-Trust Controls',
        description: 'Release of advanced zero-trust access controls',
        date: '2024-06-30',
        status: 'upcoming',
        dependencies: ['feat-6', 'feat-7']
      }
    ]
  },
  zachmanLayers,
  trizAnalysis: {
    contradictions: [
      {
        improving: 'Data security',
        worsening: 'Data accessibility',
        principles: [24, 9, 2],
        description: 'Balancing data protection with legitimate business use'
      }
    ],
    appliedPrinciples: [trizPrinciples[5], trizPrinciples[6], trizPrinciples[1]],
    innovationLevel: 9,
    recommendations: [
      'Implement data tokenization for all PII elements',
      'Use policy-based access controls with zero-trust architecture',
      'Apply encryption-in-use for sensitive operations'
    ],
    implementationStrategy: 'Security-first development with continuous compliance validation'
  },
  workflowProgress: {
    'start': true,
    'research': true,
    'market-analysis': true,
    'problem-analysis': true,
    'triz-innovation': true,
    'feature-definition': true,
    'architecture': false,
    'prioritization': false,
    'timeline': false,
    'review': false,
    'iterate': false,
    'deploy': false
  },
  exportFormats: ['json', 'pdf', 'excel', 'compliance-report']
};

export const skyflowPIIRoadmap = demoRoadmap;